import { employeesApiBaseUrl } from '../../shared/env';
import { createJsonClient, QUIET_RESTRICTION_HEADER } from '../../shared/httpClient';
import type { JobRoleGateway } from './JobRoleGateway';
import type {
  BenchmarkQuery,
  BenchmarkResult,
  BenchmarkSourceRegistryItem,
  PayBand,
  SetPayBandInput,
  CreateEmployeeJobRoleAssignmentInput,
  CreateFromTemplateInput,
  CreateJobRoleInput,
  DuplicateJobRoleInput,
  EmployeeJobRoleAssignment,
  JobRoleAuditItem,
  JobRoleDetail,
  JobRoleEmployee,
  JobRoleListItem,
  JobRoleTemplateDetail,
  JobRoleTemplateListItem,
  JobRoleVersionDetail,
  JobRoleVersionItem,
  ListJobRolesParams,
  Paged,
  PageParams,
  UpdateJobRoleInput,
  CreateReferenceOptionResult,
  JobRoleCapabilities,
  ListReferenceOptionsParams,
  OccupationDataset,
  OccupationItem,
  OccupationSearchParams,
  OccupationSearchResult,
  OccupationSystem,
  ReferenceCategory,
  ReferenceOptionList,
  RoleCodeSuggestion,
  RoleCodeSuggestionParams,
} from './types';
import {
  jobRoleStatusToWire, mapAssignment, mapAuditPage, mapBenchmarkRegistry, mapBenchmarkResult, mapCapabilities, mapCreateReferenceOption, mapEmployeePage,
  mapOccupationDatasets, mapOccupationItem, mapOccupationSearch, mapPayBandEnvelope, mapPayBandList, mapReferenceOptionList, mapRoleCodeSuggestion,
  mapRoleDetail, mapRolePage, mapTemplateDetail, mapVersionDetail, mapVersionItem, occupationSearchQuery, occupationSystemToWire, referenceOptionsQuery,
  roleCodeSuggestionQuery,
  type RawAssignment, type RawBenchmarkRegistryItem, type RawBenchmarkResult, type RawOccupationDataset, type RawOccupationItem, type RawOccupationSearch,
  type RawPayBand, type RawReferenceOptionList, type RawRoleDetail, type RawTemplateDetail, type RawVersion,
} from './wireMapping';

const enc = encodeURIComponent;

function paging(params?: PageParams): string {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.pageSize) query.set('pageSize', String(params.pageSize));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

/** `{ expectedConcurrencyVersion }` when the caller holds one (the server checks it when sent), otherwise no body at all. */
const concurrencyInit = (expected?: number): RequestInit => ({
  method: 'POST',
  ...(expected !== undefined ? { body: JSON.stringify({ expectedConcurrencyVersion: expected }) } : {}),
});

/** Talks to EPN.Employees.Api through the shared `/svc-employees` prefix, like the People gateway. The request bodies carry no
 * company id: the server takes the company from the session. Responses are mapped from the REAL wire shapes (wireMapping.ts). */
export class HttpJobRoleGateway implements JobRoleGateway {
  private readonly request = createJsonClient(employeesApiBaseUrl);

  listTemplates(search?: string, signal?: AbortSignal): Promise<JobRoleTemplateListItem[]> {
    const query = new URLSearchParams();
    if (search) query.set('search', search);
    const qs = query.toString();
    return this.request(`/api/job-role-templates${qs ? `?${qs}` : ''}`, undefined, signal);
  }

  async getTemplate(id: string, signal?: AbortSignal): Promise<JobRoleTemplateDetail> {
    return mapTemplateDetail(await this.request<RawTemplateDetail>(`/api/job-role-templates/${enc(id)}`, undefined, signal));
  }

  async listRoles(params?: ListJobRolesParams, signal?: AbortSignal): Promise<Paged<JobRoleListItem>> {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.departmentId) query.set('departmentId', params.departmentId);
    if (params?.jobFamily) query.set('jobFamily', params.jobFamily);
    if (params?.careerLevel) query.set('careerLevel', params.careerLevel);
    if (params?.status !== undefined) query.set('status', jobRoleStatusToWire(params.status));
    if (params?.payType) query.set('payType', params.payType);
    if (params?.sort) query.set('sort', params.sort);
    if (params?.page) query.set('page', String(params.page));
    if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    const qs = query.toString();
    return mapRolePage(await this.request(`/api/job-roles${qs ? `?${qs}` : ''}`, undefined, signal));
  }

  async getRole(id: string, signal?: AbortSignal): Promise<JobRoleDetail> {
    return mapRoleDetail(await this.request<RawRoleDetail>(`/api/job-roles/${enc(id)}`, undefined, signal));
  }

  async createRole(input: CreateJobRoleInput): Promise<JobRoleDetail> {
    return mapRoleDetail(await this.request<RawRoleDetail>('/api/job-roles', { method: 'POST', body: JSON.stringify(input) }));
  }

  async createRoleFromTemplate(templateId: string, input: CreateFromTemplateInput = {}): Promise<JobRoleDetail> {
    return mapRoleDetail(await this.request<RawRoleDetail>(`/api/job-roles/from-template/${enc(templateId)}`, { method: 'POST', body: JSON.stringify(input) }));
  }

  async duplicateRole(id: string, input: DuplicateJobRoleInput): Promise<JobRoleDetail> {
    return mapRoleDetail(await this.request<RawRoleDetail>(`/api/job-roles/${enc(id)}/duplicate`, { method: 'POST', body: JSON.stringify(input) }));
  }

  async updateRole(id: string, input: UpdateJobRoleInput): Promise<JobRoleDetail> {
    return mapRoleDetail(await this.request<RawRoleDetail>(`/api/job-roles/${enc(id)}`, { method: 'PUT', body: JSON.stringify(input) }));
  }

  async createVersion(id: string, expectedConcurrencyVersion?: number): Promise<JobRoleDetail> {
    return mapRoleDetail(await this.request<RawRoleDetail>(`/api/job-roles/${enc(id)}/versions`, concurrencyInit(expectedConcurrencyVersion)));
  }

  async activateRole(id: string, expectedConcurrencyVersion: number): Promise<JobRoleDetail> {
    return mapRoleDetail(await this.request<RawRoleDetail>(`/api/job-roles/${enc(id)}/activate`, concurrencyInit(expectedConcurrencyVersion)));
  }

  async archiveRole(id: string, expectedConcurrencyVersion?: number): Promise<JobRoleDetail> {
    return mapRoleDetail(await this.request<RawRoleDetail>(`/api/job-roles/${enc(id)}/archive`, concurrencyInit(expectedConcurrencyVersion)));
  }

  async restoreRole(id: string, expectedConcurrencyVersion?: number): Promise<JobRoleDetail> {
    return mapRoleDetail(await this.request<RawRoleDetail>(`/api/job-roles/${enc(id)}/restore`, concurrencyInit(expectedConcurrencyVersion)));
  }

  async listVersions(id: string, signal?: AbortSignal): Promise<JobRoleVersionItem[]> {
    return (await this.request<RawVersion[]>(`/api/job-roles/${enc(id)}/versions`, undefined, signal)).map(mapVersionItem);
  }

  async getVersion(id: string, versionNumber: number, signal?: AbortSignal): Promise<JobRoleVersionDetail> {
    return mapVersionDetail(await this.request<RawVersion>(`/api/job-roles/${enc(id)}/versions/${versionNumber}`, undefined, signal));
  }

  async listRoleEmployees(id: string, params?: PageParams, signal?: AbortSignal): Promise<Paged<JobRoleEmployee>> {
    return mapEmployeePage(await this.request(`/api/job-roles/${enc(id)}/employees${paging(params)}`, undefined, signal));
  }

  async listRoleAudit(id: string, params?: PageParams, signal?: AbortSignal): Promise<Paged<JobRoleAuditItem>> {
    return mapAuditPage(await this.request(`/api/job-roles/${enc(id)}/audit${paging(params)}`, undefined, signal));
  }

  async assignEmployeeRole(employeeId: string, input: CreateEmployeeJobRoleAssignmentInput): Promise<EmployeeJobRoleAssignment> {
    return mapAssignment(await this.request<RawAssignment>(`/api/employees/${enc(employeeId)}/job-role-assignments`, { method: 'POST', body: JSON.stringify(input) }));
  }

  async listEmployeeRoleAssignments(employeeId: string, signal?: AbortSignal): Promise<EmployeeJobRoleAssignment[]> {
    return (await this.request<RawAssignment[]>(`/api/employees/${enc(employeeId)}/job-role-assignments`, undefined, signal)).map(mapAssignment);
  }

  // ---- PR 2: market benchmarks and employer pay bands. No company id or user id is ever sent: the server takes both from the session. ----

  /** `quiet` marks a background lookup: a plan refusal (Pro Plus needed) is the caller's to handle and raises no app-wide notice. */
  async getBenchmarks(roleId: string, query?: BenchmarkQuery, signal?: AbortSignal, options?: { quiet?: boolean }): Promise<BenchmarkResult> {
    const params = new URLSearchParams();
    if (query?.geographyType) params.set('geographyType', query.geographyType);
    if (query?.state) params.set('state', query.state);
    if (query?.metroCode) params.set('metroCode', query.metroCode);
    if (query?.referenceYear !== undefined) params.set('referenceYear', String(query.referenceYear));
    if (query?.basis) params.set('basis', query.basis);
    if (query?.annualHours !== undefined) params.set('annualHours', String(query.annualHours));
    const qs = params.toString();
    const init = options?.quiet ? { headers: { [QUIET_RESTRICTION_HEADER]: '1' } } : undefined;
    return mapBenchmarkResult(await this.request<RawBenchmarkResult>(`/api/job-roles/${enc(roleId)}/compensation-benchmarks${qs ? `?${qs}` : ''}`, init, signal));
  }

  async listBenchmarkSources(signal?: AbortSignal): Promise<BenchmarkSourceRegistryItem[]> {
    return mapBenchmarkRegistry(await this.request<RawBenchmarkRegistryItem[]>('/api/compensation-benchmark-sources', undefined, signal));
  }

  async getPayBand(roleId: string, signal?: AbortSignal): Promise<PayBand | null> {
    return mapPayBandEnvelope(await this.request<{ band: RawPayBand | null }>(`/api/job-roles/${enc(roleId)}/pay-band`, undefined, signal));
  }

  async listPayBandHistory(roleId: string, signal?: AbortSignal): Promise<PayBand[]> {
    return mapPayBandList(await this.request<RawPayBand[]>(`/api/job-roles/${enc(roleId)}/pay-band/history`, undefined, signal));
  }

  async setPayBand(roleId: string, input: SetPayBandInput): Promise<PayBand> {
    const band = mapPayBandEnvelope(await this.request<{ band: RawPayBand } | RawPayBand>(`/api/job-roles/${enc(roleId)}/pay-band`, { method: 'PUT', body: JSON.stringify(input) }));
    if (band) return band;
    // The contract does not say what the PUT answers with; when it sends no band, the saved one is read back.
    const saved = await this.getPayBand(roleId);
    if (!saved) throw new Error('The pay band was saved but could not be read back.');
    return saved;
  }

  async endPayBand(roleId: string, expectedConcurrencyVersion: number): Promise<void> {
    await this.request<unknown>(`/api/job-roles/${enc(roleId)}/pay-band?expectedConcurrencyVersion=${enc(String(expectedConcurrencyVersion))}`, { method: 'DELETE' });
  }

  // ---- guided fields: no company id is ever sent; the server takes it from the session ----

  async getCapabilities(signal?: AbortSignal): Promise<JobRoleCapabilities> {
    return mapCapabilities(await this.request<Partial<JobRoleCapabilities>>('/api/job-role-capabilities', undefined, signal));
  }

  async searchOccupations(params: OccupationSearchParams, signal?: AbortSignal): Promise<OccupationSearchResult> {
    return mapOccupationSearch(await this.request<RawOccupationSearch>(`/api/reference/occupations?${occupationSearchQuery(params)}`, undefined, signal));
  }

  async getOccupation(system: OccupationSystem, code: string, signal?: AbortSignal): Promise<OccupationItem> {
    return mapOccupationItem(await this.request<RawOccupationItem>(`/api/reference/occupations/${enc(occupationSystemToWire(system))}/${enc(code)}`, undefined, signal));
  }

  async listOccupationDatasets(signal?: AbortSignal): Promise<OccupationDataset[]> {
    return mapOccupationDatasets(await this.request<RawOccupationDataset[]>('/api/reference/occupation-datasets', undefined, signal));
  }

  async listReferenceOptions(params: ListReferenceOptionsParams, signal?: AbortSignal): Promise<ReferenceOptionList> {
    return mapReferenceOptionList(await this.request<RawReferenceOptionList>(`/api/job-role-reference-options?${referenceOptionsQuery(params)}`, undefined, signal));
  }

  async createReferenceOption(input: { category: ReferenceCategory; name: string }): Promise<CreateReferenceOptionResult> {
    return mapCreateReferenceOption(await this.request('/api/job-role-reference-options', { method: 'POST', body: JSON.stringify({ category: input.category, name: input.name }) }));
  }

  async suggestRoleCode(params: RoleCodeSuggestionParams, signal?: AbortSignal): Promise<RoleCodeSuggestion> {
    return mapRoleCodeSuggestion(await this.request(`/api/job-roles/role-code-suggestion?${roleCodeSuggestionQuery(params)}`, undefined, signal));
  }
}
